import { Show } from "solid-js";
import { z } from "zod";

import Trans from "@repo/localization/jsx.macro";
import t from "@repo/localization/string.macro";
import Logo from "@repo/ui/branding/Logo";
import LoadableButton from "@repo/ui/buttons/LoadableButton";
import Alert from "@repo/ui/info/Alert";
import Form from "@repo/ui/inputs/Form";
import TextInput from "@repo/ui/inputs/TextInput";
import Link from "@repo/ui/navigation/Link";
import createForm from "@repo/ui/signals/createForm";
import Log from "@repo/utils/Log";

import { track } from "~/analytics/client";
import appSettings from "~/appSettings";
import AuthButtons from "~/components/auth/AuthButtons";
import AuthLayout from "~/components/auth/AuthLayout";
import PasswordInput from "~/components/auth/PasswordInput";
import { useUser } from "~/state/features/user";

export default function Login() {
  return (
    <AuthLayout.Login>
      <Logo class="text:heading-3" word icon />
      <header class="text:heading-3++ mt-3 mb-2">
        <Trans>Log in to your account</Trans>
      </header>
      <Show when={appSettings.auth.mfa}>
        <Alert
          type="info"
          dugout
          propsFor={{ icon: { class: "icon:medium" } }}
          icon=""
          class="my-4 p-2.5 text:small"
        >
          <header class="text:regular+ mb-1">
            <Trans>All logins now require verification by SMS or email</Trans>
          </header>
          <Trans>
            For your security, you will verify by code after entering your email
            and password. Have trouble logging in?{" "}
            <Link.External
              href="https://kikoffsupport.zendesk.com/hc/en-us/articles/22697860176525-Kikoff-login-now-requires-two-factor-authentication"
              class="underline"
            >
              Get help
            </Link.External>
          </Trans>
        </Alert>
      </Show>
      <LoginForm />
      <Link href="/signup" variant="container" class="my-3">
        <Trans>
          Don't have an account?{" "}
          <span class="underline text:regular+">Sign up here</span>
        </Trans>
      </Link>
    </AuthLayout.Login>
  );
}

function LoginForm() {
  const [, setUser] = useUser();

  const { register, handleSubmit, submittable } = createForm({
    schema: z.object({
      email: z.string({ required_error: t`Required` }).email(t`Invalid email`),
      password: z.string({ required_error: t`Required` }),
    }),
  });

  return (
    <Form
      onSubmit={(e) => {
        track.login.submitButton("click");

        return handleSubmit((_, data) => {
          return setUser.login
            .defer(data)
            .then((resolve) => {
              LoadableButton.afterSuccess(() => {
                resolve();
              });
            })
            .catch((e) => {
              Log.error(e);
              throw e;
            });
        })(e);
      }}
    >
      <TextInput
        class="mb-1"
        label={t`Email`}
        type="email"
        autocomplete="email"
        {...register("email")}
      />
      <PasswordInput
        label={t`Password`}
        autocomplete="current-password"
        {...register("password")}
      />
      <div class="column-start gap-2 my-3">
        <Link class="text:mini+ underline" href="/auth/reset-password">
          <Trans>Forgot your password?</Trans>
        </Link>
        <Show when={appSettings.auth.forgotEmail}>
          <Link href="/auth/forgot-email" class="text:mini+ underline">
            <Trans>Forgot your email?</Trans>
          </Link>
        </Show>
      </div>
      <AuthButtons
        submittable={submittable()}
        text={{
          initial: t`Log in`,
          pending: t`Logging in...`,
          success: t`Logged in`,
        }}
      />
    </Form>
  );
}
